import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import PreLogin from './pages/PreLogin.jsx'
import Home from './pages/Home.jsx'
import Login from './pages/Login.jsx'
import Register from './pages/Register.jsx'
import OAuthCallback from './pages/OAuthCallback.jsx'
import Dashboard from './pages/Dashboard.jsx'
import Communication from './pages/Communication.jsx'
import GmailTest from './pages/GmailTest.jsx'
import Cgu from './pages/Cgu.jsx'
import PrivacyPolicy from './pages/PrivacyPolicy.jsx'
import LegalNotice from './pages/LegalNotice.jsx'
import {
  SergeProjectsPage,
  SergeWorkspaceLayout,
  SergeUploadPage,
  SergeAnalysePage,
  SergeClarificationPage,
  SergeRisksPage,
  SergeExportPage,
  LegalEntitiesPage,
} from './pages/SergeProjects'
import ProtectedRoute from './components/common/ProtectedRoute.jsx'
import ModuleRoute from './components/common/ModuleRoute.jsx'
import RedirectTo from './components/common/RedirectTo.jsx'
import ProspectionPage from './features/prospection/pages/ProspectionPage.jsx'
import CrmPage from './features/crm/pages/CrmPage.jsx'
import AccountPage from './features/account/pages/AccountPage.jsx'
import ContractsPage from './features/contracts/pages/ContractsPage.jsx'
import AdminLoginPage from './features/admin/pages/AdminLoginPage.jsx'
import { ADMIN_LOGIN_PATH } from './features/admin/config.js'
import Navbar from './components/layout/Navbar.jsx'
import DashboardLayout from './components/layout/DashboardLayout.jsx'

export default function App() {
  return (
    <>
      <Toaster
        position="top-right"
        containerStyle={{
          top: 80,
          right: 20,
          maxHeight: '40vh',
          overflow: 'hidden'
        }}
        toastOptions={{
          // Styles par défaut
          duration: 3000,
          style: {
            background: 'rgb(var(--rgb-panel) / 0.95)',
            color: '#5b5cf4',
            border: '1px solid rgba(91, 92, 244, 0.3)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(91, 92, 244, 0.15)',
            fontFamily: 'inherit',
            fontSize: '13px',
            padding: '12px 16px',
            maxWidth: '350px',
          },
          // Success
          success: {
            iconTheme: {
              primary: '#5b5cf4',
              secondary: 'rgb(var(--rgb-canvas))',
            },
            style: {
              border: '1px solid #5b5cf4',
              boxShadow: '0 8px 32px rgba(91, 92, 244, 0.25)',
            },
          },
          // Error
          error: {
            iconTheme: {
              primary: '#ff4444',
              secondary: 'rgb(var(--rgb-canvas))',
            },
            style: {
              color: '#ff4444',
              border: '1px solid rgba(255, 68, 68, 0.3)',
              boxShadow: '0 8px 32px rgba(255, 68, 68, 0.15)',
            },
          },
          // Loading
          loading: {
            iconTheme: {
              primary: 'rgb(var(--rgb-aqua))',
              secondary: 'rgb(var(--rgb-canvas))',
            },
            style: {
              color: 'rgb(var(--rgb-aqua))',
              border: '1px solid rgb(var(--rgb-aqua) / 0.3)',
            },
          },
        }}
        gutter={8}
      />
      <Routes>
        {/* Unified Auth page */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        
        {/* OAuth callback - must be accessible without auth */}
        <Route path="/oauth/callback" element={<OAuthCallback />} />

        {/* Connexion admin discrète - non liée depuis le reste du site */}
        <Route path={ADMIN_LOGIN_PATH} element={<AdminLoginPage />} />

        {/* Public legal pages */}
        <Route path="/legal" element={<Navigate to="/legal/mentions-legales" replace />} />
        <Route path="/legal/cgu" element={<Cgu />} />
        <Route path="/legal/politique-de-confidentialite" element={<PrivacyPolicy />} />
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/mentions-legales" element={<LegalNotice />} />

        {/* Protected routes - require auth */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          {/* Prospection : toutes les recherches. CRM : tous les résultats (droits gérés dans chaque page). */}
          <Route path="prospection" element={<ProspectionPage />} />
          <Route path="prospection/:source" element={<ProspectionPage />} />
          <Route path="crm" element={<CrmPage />} />
          <Route path="crm/:base" element={<CrmPage />} />

          {/* Anciennes adresses (liens, favoris, notifications) */}
          <Route path="prospects" element={<RedirectTo to="/dashboard/prospection/b2b" />} />
          <Route path="immobilier" element={<RedirectTo to="/dashboard/prospection/immobilier" />} />
          <Route path="my-prospects" element={<RedirectTo to="/dashboard/crm/prospects" />} />
          <Route path="france-travail-candidats" element={<RedirectTo to="/dashboard/crm/candidats-ft" />} />
          <Route path="projects-auto" element={<RedirectTo to="/dashboard/crm/projets-auto" />} />
          <Route path="dormant-projects" element={<RedirectTo to="/dashboard/crm/relances" />} />
          <Route path="projets" element={<RedirectTo to="/dashboard/serge-projects" />} />

          <Route path="communication" element={<ModuleRoute module="communication"><Communication /></ModuleRoute>} />
          {/* Compte : crédits, activités, organisation, entreprises, administration (droits gérés dans la page) */}
          <Route path="contrats" element={<ModuleRoute module="prospects"><ContractsPage /></ModuleRoute>} />
          <Route path="contrats/devis/:quoteId" element={<ModuleRoute module="prospects"><ContractsPage /></ModuleRoute>} />
          <Route path="contrats/contrats/:contractId" element={<ModuleRoute module="prospects"><ContractsPage /></ModuleRoute>} />
          <Route path="contrats/:section" element={<ModuleRoute module="prospects"><ContractsPage /></ModuleRoute>} />
          <Route path="compte" element={<AccountPage />} />
          <Route path="compte/:section" element={<AccountPage />} />
          <Route path="buy-credits" element={<RedirectTo to="/dashboard/compte/credits" />} />
          <Route path="activity" element={<RedirectTo to="/dashboard/compte/activite" />} />
          <Route path="organization" element={<RedirectTo to="/dashboard/compte/organisation" />} />
          <Route path="agencies" element={<RedirectTo to="/dashboard/compte/entreprise" />} />
          <Route path="admin" element={<RedirectTo to="/dashboard/compte/admin" />} />
          <Route path="gmail-test" element={<GmailTest />} />

          {/* Offre : liste des projets Serge (le workspace d'un projet est hors DashboardLayout) */}
          <Route path="serge-projects" element={<SergeProjectsPage />} />
        </Route>

        {/* Serge Workspace - Standalone layout outside DashboardLayout */}
        <Route
          path="/dashboard/serge-projects/:projectId"
          element={
            <ProtectedRoute>
              <SergeWorkspaceLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SergeUploadPage />} />
          <Route path="upload" element={<SergeUploadPage />} />
          <Route path="analyse" element={<SergeAnalysePage />} />
          <Route path="clarification" element={<SergeClarificationPage />} />
          <Route path="risks" element={<SergeRisksPage />} />
          <Route path="export" element={<SergeExportPage />} />
        </Route>

        {/* Legal Entities - Standalone Serge page */}
        <Route
          path="/dashboard/legal-entities"
          element={
            <ProtectedRoute>
              <LegalEntitiesPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback - redirect to prelogin */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
